/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ReaderState } from '@backstage/plugin-techdocs';
import type { FC } from 'react';

import { Alert, Button, CircularProgress } from '@mui/material';
import { tss } from 'tss-react/mui';

import { TechDocsBuildLogs } from './TechDocsBuildLogs';
import { TechDocsNotFound } from './TechDocsNotFound';

const useStyles = tss.create(({ theme }) => ({
  message: {
    overflowWrap: 'anywhere',
    // `word-break: break-word` is deprecated, but gives legacy support to browsers not supporting `overflow-wrap` yet
    // https://developer.mozilla.org/en-US/docs/Web/CSS/word-break
    wordBreak: 'break-word',
  },
  root: {
    marginBottom: theme.spacing(2),
  },
}));

export const TechDocsStateIndicator: FC<ReaderState> = ({
  buildLog,
  contentErrorMessage,
  contentReload,
  state,
  syncErrorMessage,
}) => {
  let StateAlert: null | React.ReactNode = null;
  const { classes } = useStyles();

  if (state === 'INITIAL_BUILD') {
    StateAlert = (
      <Alert
        action={<TechDocsBuildLogs buildLog={buildLog} />}
        classes={{ root: classes.root }}
        icon={<CircularProgress size="24px" />}
        severity="info"
        variant="outlined"
      >
        Documentation is accessed for the first time and is being prepared. The
        subsequent loads are much faster.
      </Alert>
    );
  }

  if (state === 'CONTENT_STALE_REFRESHING') {
    StateAlert = (
      <Alert
        action={<TechDocsBuildLogs buildLog={buildLog} />}
        classes={{ root: classes.root }}
        icon={<CircularProgress size="24px" />}
        severity="info"
        variant="outlined"
      >
        A newer version of this documentation is being prepared and will be
        available shortly.
      </Alert>
    );
  }

  if (state === 'CONTENT_STALE_READY') {
    StateAlert = (
      <Alert
        action={(
          <Button color="inherit" onClick={() => contentReload()}>
            Refresh
          </Button>
        )}
        classes={{ root: classes.root }}
        severity="success"
        variant="outlined"
      >
        A newer version of this documentation is now available, please refresh
        to view.
      </Alert>
    );
  }

  if (state === 'CONTENT_STALE_ERROR') {
    StateAlert = (
      <Alert
        action={<TechDocsBuildLogs buildLog={buildLog} />}
        classes={{ message: classes.message, root: classes.root }}
        severity="error"
        variant="outlined"
      >
        Building a newer version of this documentation failed.
        {' '}
        {syncErrorMessage}
      </Alert>
    );
  }

  if (state === 'CONTENT_NOT_FOUND') {
    StateAlert = (
      <>
        {syncErrorMessage && (
          <Alert
            action={<TechDocsBuildLogs buildLog={buildLog} />}
            classes={{ message: classes.message, root: classes.root }}
            severity="error"
            variant="outlined"
          >
            Building a newer version of this documentation failed.
            {' '}
            {syncErrorMessage}
          </Alert>
        )}
        <TechDocsNotFound errorMessage={contentErrorMessage} />
      </>
    );
  }

  return StateAlert;
};
